import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { mainApi } from "../../api/axios";

/**
 * Fetch all customers
 */
export const getCustomers = createAsyncThunk(
  "customer/getCustomers",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(`/v1/pos/customers?store_id=${storeId}`);
      const responseData = res.data?.data ?? res.data ?? {};
      return {
        customers: responseData.customers ?? [],
        pagination: responseData.pagination ?? { limit: 0, offset: 0, total: 0 },
      };
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Get customer details
 */
export const getCustomerDetails = createAsyncThunk(
  "customer/getCustomerDetails",
  async ({ storeId, uid }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/customers/details?store_id=${storeId}&uid=${uid}`,
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Add customer
 */
export const addCustomer = createAsyncThunk(
  "customer/addCustomer",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await mainApi.post("/v1/pos/customers", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Update customer
 */
export const updateCustomer = createAsyncThunk(
  "customer/updateCustomer",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await mainApi.put("/v1/pos/customers", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Search customers
 */
export const searchCustomer = createAsyncThunk(
  "customer/searchCustomer",
  async ({ mobileNo }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/customers/search?mobile_no=${mobileNo}`,
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Perform customer action (activate/deactivate, etc.)
 */
export const customerAction = createAsyncThunk(
  "customer/customerAction",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await mainApi.patch("/v1/pos/customers/actions", obj);
      return obj; // return the action payload for updating state
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// Slice
const customerSlice = createSlice({
  name: "customer",
  initialState: {
    list: [],
    pagination: { limit: 0, offset: 0, total: 0 },
    searchResults: {},
    loading: false,
    error: null,
  },
  reducers: {
    clearCustomerState: (state) => {
      state.list = [];
      state.pagination = { limit: 0, offset: 0, total: 0 };
      state.searchResults = {};
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.customers;
        state.pagination = action.payload.pagination;
      })
      .addCase(getCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ADD
      .addCase(addCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = {};
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.loading = false;
      })

      // UPDATE
      .addCase(updateCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = {};
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.loading = false;
      })
      // SEARCH
      .addCase(searchCustomer.fulfilled, (state, action) => {
        state.searchResults = action.payload;
      })

      // ACTION
      .addCase(customerAction.fulfilled, (state, action) => {
        const updated = action.payload;
        state.list = state.list.map((c) =>
          c.id === updated.id ? { ...c, ...updated } : c,
        );
      });
  },
});

export const { clearCustomerState } = customerSlice.actions;
export default customerSlice.reducer;
